import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ShipmentLogbookData,
  ShipmentLogbookScope,
} from 'common/src/api/shipment-logbook';
import {
  getShipmentLogbook,
  addShipmentLogbookNote,
  uploadShipmentNotePhoto,
} from 'common/src/api/shipment-logbook';
import { localCalendarDate } from '../utils/shipment-logbook';
import { useTranslation } from 'common/src/hooks/useTranslation';

interface Options {
  shipment: ShipmentLogbookScope;
  userId?: number;
  authId?: string;
  active: boolean;
  onBusyChange: (busy: boolean) => void;
  onChanged?: () => void;
  refreshVersion?: number;
}
export function useShipmentLogbook(options: Options) {
  const { shipment, userId, authId, active } = options;
  const { t } = useTranslation('shipments');
  const scope =
    active && userId && authId
      ? JSON.stringify([
          authId,
          userId,
          shipment.id,
          shipment.exporter_id,
          shipment.season,
          shipment.species_id,
        ])
      : null;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const callbacks = useRef(options);
  callbacks.current = options;
  const previousScope = useRef<string | null>(null);
  const mounted = useRef(false),
    readVersion = useRef(0),
    writeRef = useRef<object | null>(null);
  const noteIntent = useRef<{
    key: string;
    id: string;
    uploaded: Set<number>;
    files: File[];
  } | null>(null);
  const [data, setData] = useState<ShipmentLogbookData | null>(null),
    [loadedScope, setLoadedScope] = useState<string | null>(null);
  const [loading, setLoading] = useState(false),
    [readError, setReadError] = useState<string | null>(null),
    [writeError, setWriteError] = useState<string | null>(null);
  const [noteCommitted, setNoteCommitted] = useState(false);
  const [busy, setBusy] = useState(false),
    [note, setNote] = useState(''),
    [photos, setPhotos] = useState<File[]>([]);
  const loadLogbook = useCallback(async () => {
    if (!scope) return;
    const version = ++readVersion.current;
    const current = () =>
      mounted.current &&
      scopeRef.current === scope &&
      readVersion.current === version;
    setLoading(true);
    setReadError(null);
    try {
      const result = await getShipmentLogbook(shipment);
      if (current()) {
        setData(result);
        setLoadedScope(scope);
      }
    } catch {
      if (current())
        setReadError(t('No se pudo cargar la bitácora del despacho.'));
    } finally {
      if (current()) setLoading(false);
    }
  }, [scope, shipment, t]);
  const invalidateRequests = useCallback(() => {
    readVersion.current++;
    writeRef.current = null;
    callbacks.current.onBusyChange(false);
  }, []);
  // Callers key the panel by shipment and user; the ref guard also handles a
  // hidden/closing modal before its children finish unmounting.
  useEffect(() => {
    mounted.current = true;
    if (previousScope.current !== scope) {
      previousScope.current = scope;
      noteIntent.current = null;
      setNote('');
      setPhotos([]);
      setWriteError(null);
      setNoteCommitted(false);
      setBusy(false);
    }
    if (scope) void loadLogbook();
    return () => {
      mounted.current = false;
      invalidateRequests();
    };
  }, [scope, loadLogbook, invalidateRequests]);
  useEffect(() => {
    if (options.refreshVersion) void loadLogbook();
  }, [options.refreshVersion, loadLogbook]);
  const handleSaveNote = useCallback(async () => {
    if (
      !scope ||
      !userId ||
      writeRef.current ||
      loading ||
      readError ||
      !data?.state.can_read_notes ||
      (!note.trim() && !photos.length)
    )
      return;
    const token = {};
    writeRef.current = token;
    const current = () =>
      mounted.current &&
      scopeRef.current === scope &&
      writeRef.current === token;
    const key = JSON.stringify([
      scope,
      note.trim(),
      photos.map((f) => [f.name, f.size, f.lastModified]),
    ]);
    if (
      noteIntent.current?.key !== key ||
      !photos.every((file, index) => noteIntent.current?.files[index] === file)
    )
      noteIntent.current = {
        key,
        id: crypto.randomUUID(),
        uploaded: new Set(),
        files: photos,
      };
    const intent = noteIntent.current;
    setBusy(true);
    setWriteError(null);
    callbacks.current.onBusyChange(true);
    let committed = false;
    try {
      const id = await addShipmentLogbookNote(
        shipment,
        note.trim(),
        localCalendarDate(),
        intent.id,
        photos.length > 0
      );
      committed = true;
      if (current()) setNoteCommitted(true);
      for (let i = 0; i < photos.length; i++)
        if (!intent.uploaded.has(i)) {
          await uploadShipmentNotePhoto(
            id,
            shipment.exporter_id,
            userId,
            photos[i],
            `${intent.id}-${i}`
          );
          intent.uploaded.add(i);
        }
      if (!current()) return;
      noteIntent.current = null;
      setNoteCommitted(false);
      setNote('');
      setPhotos([]);
      callbacks.current.onChanged?.();
      await loadLogbook();
    } catch {
      if (current())
        setWriteError(
          t(
            committed || noteCommitted
              ? 'La nota se guardó, pero faltan fotos. Reintenta para completar los adjuntos.'
              : 'No se pudo completar el guardado. Tu nota y fotos siguen aquí para reintentar.'
          )
        );
    } finally {
      if (current()) {
        writeRef.current = null;
        setBusy(false);
        callbacks.current.onBusyChange(false);
      }
    }
  }, [
    scope,
    userId,
    loading,
    readError,
    data,
    note,
    photos,
    noteCommitted,
    shipment,
    loadLogbook,
    t,
  ]);
  return {
    data: loadedScope === scope ? data : null,
    loading: !!scope && (loading || (loadedScope !== scope && !readError)),
    readError,
    writeError,
    busy,
    noteCommitted,
    note,
    setNote,
    photos,
    setPhotos,
    loadLogbook,
    handleSaveNote,
  };
}
